import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { fetchWithAuth } from '../utils/fetchWithAuth';

const AuthContext = createContext();

// Lista de rutas públicas que no requieren autenticación
const publicRoutes = [
  "/",
  "/unauthorized",
  "/request-password",
  "/verify-password",
  "/reset-password"
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  // Verifica si el token es válido
  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const now = Date.now();
      const expiry = decoded.exp * 1000;
      
      if (expiry <= now) {
        console.warn("Token expirado. Hora actual:", new Date(now), "Expira:", new Date(expiry));
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error decodificando token:", error);
      return false;
    }
  };

  // Verifica si la ruta actual es pública
  const isPublicRoute = () => {
    return publicRoutes.includes(location.pathname);
  };

  // Cargar usuario desde localStorage
  const loadUser = () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token || !isTokenValid(token)) return false;

      const decoded = jwtDecode(token);
      const rut = localStorage.getItem("rut");
      const trade_name = localStorage.getItem("trade_name");
      const business_name = localStorage.getItem("business_name");

      setUser({
        token,
        role: decoded.role,
        business_name: business_name || decoded.business_name,
        rut,
        trade_name
      });

      return true;
    } catch (error) {
      console.error("Error loading user:", error);
      return false;
    }
  };

  // Inicializar autenticación
  const initializeAuth = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");

      if (token && isTokenValid(token)) {
        // token válido, cargar desde localStorage
        loadUser();
      } else {
        // intentar refresh
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          const { accessToken } = data;

          if (accessToken && isTokenValid(accessToken)) {
            localStorage.setItem("accessToken", accessToken);
            const decoded = jwtDecode(accessToken);

            setUser({
              token: accessToken,
              role: decoded.role,
              business_name: decoded.business_name,
              rut: decoded.rut,
              trade_name: decoded.trade_name,
            });
          }
        } else {
          // refresh inválido
          localStorage.clear();
          setUser(null);
        }
      }
    } catch (error) {
      console.error("Error en initializeAuth:", error);
      localStorage.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Función para iniciar sesión
  const login = (token) => {
    if (token && isTokenValid(token)) {
      localStorage.setItem("accessToken", token);
      const decoded = jwtDecode(token);
      setUser({
        token,
        role: decoded.role,
        business_name: decoded.business_name
      });
      return true;
    }
    return false;
  };

  // Función para cerrar sesión
  const logout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    } finally {
      // Siempre limpia localStorage y contexto
      localStorage.removeItem("accessToken");
      localStorage.removeItem("business_name");
      localStorage.removeItem("rut");
      localStorage.removeItem("trade_name");
      // Limpiar flags de pop-up para que se muestre en la próxima sesión
      sessionStorage.removeItem("admin_popup_shown");
      setUser(null);

      if (!isPublicRoute()) {
        navigate("/");
      }
    }
  };

  // Función para manejar redirección con delay
  const handleRedirect = () => {
    setRedirecting(true);
    setTimeout(() => {
      navigate("/");
    }, 3000);
  };

  // Inicializar al montar el componente (sólo una vez)
  useEffect(() => {
    initializeAuth();
  }, []);

  // Verificar cuando cambia la ruta si necesitamos autenticación
  useEffect(() => {
    // Si no es una ruta pública y no hay usuario, redirigir al inicio
    if (!user && !loading && !isPublicRoute() && !redirecting) {
      handleRedirect();
    }
  }, [location.pathname, user, loading]);

  // Pantalla de carga
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-gray-600">Cargando...</p>
      </div>
    );
  }

  // Pantalla de sesión no válida con mensaje de redirección
  if (!user && !loading && !isPublicRoute()) {
    const clientDate = new Date();
    
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <p className="text-lg text-red-600 mb-4">Sesión no válida. Redirigiendo...</p>
          <div className="text-sm text-gray-600 space-y-2">
            <p>Verifica que la hora de tu dispositivo esté correctamente sincronizada:</p>
            <p className="font-mono bg-gray-100 p-2 rounded">
              {clientDate.toLocaleString('es-CL', { 
                timeZone: 'America/Santiago',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </p>
            <p className="text-xs text-gray-500">
              Si el problema persiste, contacta al administrador del sistema.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loadUser, logout, login, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para acceder al contexto en otros componentes
export const useAuth = () => useContext(AuthContext);